import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import {
  Container, Paper, Typography, Button, TextField, Grid, Dialog,
  DialogTitle, DialogContent, DialogActions, Switch,
  FormControlLabel, Divider, IconButton, Tooltip, CircularProgress,
  Chip, makeStyles, Collapse, MenuItem, Select, InputLabel, FormControl
} from "@material-ui/core";
import {
  VideoCall, MeetingRoom, ExitToApp, Stop, Refresh,
  FileCopy, FiberManualRecord, Close, WhatsApp, History, Link as LinkIcon,
  ExpandMore, ExpandLess, Palette
} from "@material-ui/icons";
import {
  createBBBMeeting,
  joinBBBMeeting,
  listBBBMeetings,
  endBBBMeeting,
  getBBBMeetingHistory
} from "../../services/api.new-features";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  statCard: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.08)",
    background: "#fff"
  },
  statLabel: {
    fontSize: 12,
    color: "#757575",
    marginBottom: theme.spacing(0.5)
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.1
  },
  sectionPaper: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 14
  },
  meetingCard: {
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: 10,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    background: "#fff",
    transition: "box-shadow 0.2s ease",
    "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.10)" }
  },
  historyCard: {
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: 10,
    padding: theme.spacing(1.5),
    background: "#fff",
    height: "100%"
  },
  runningDot: { color: "#4caf50", fontSize: 14, marginRight: 4 },
  stoppedDot: { color: "#9e9e9e", fontSize: 14, marginRight: 4 },
  commandBox: {
    background: "rgba(255,255,255,0.7)",
    borderRadius: 6,
    padding: theme.spacing(1.5),
    fontFamily: "monospace",
    fontSize: 13,
    marginBottom: theme.spacing(0.5),
    border: "1px solid rgba(46,125,50,0.12)"
  },
  commandLabel: { color: "#1565c0", fontWeight: 700 },
  commandDesc: { color: "#555", fontSize: 12, marginLeft: 8 },
  liveBadge: {
    background: "#e8f5e9",
    border: "1px solid #4caf50",
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 11,
    color: "#2e7d32",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    marginLeft: 8
  },
  inactiveBadge: {
    background: "#f5f5f5",
    border: "1px solid #bbb",
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 11,
    color: "#757575",
    marginLeft: 8
  },
  refreshText: { fontSize: 11, color: "#9e9e9e", marginLeft: 8 },
  sectionTitle: { fontWeight: 700, marginBottom: theme.spacing(1), fontSize: 15 },
  whatsappSection: {
    background: "linear-gradient(180deg, #edf8ed 0%, #e8f5e9 100%)",
    border: "1px solid #c8e6c9",
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: "#9e9e9e"
  },
  metaLine: {
    fontSize: 12,
    color: "#757575",
    marginTop: theme.spacing(0.5)
  },
  historyActions: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  smallMuted: {
    fontSize: 12,
    color: "#757575"
  },
  stylingSection: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    background: "#f8f9fa",
    borderRadius: 8,
    border: "1px solid #e0e0e0"
  },
  stylingHeader: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    gap: 6,
    color: "#555",
    "&:hover": { color: "#333" }
  },
  colorInputRow: {
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  colorSwatch: {
    width: 36,
    height: 36,
    borderRadius: 6,
    border: "2px solid #ddd",
    cursor: "pointer",
    flexShrink: 0
  }
}));

const COMMANDS = [
  { cmd: "#reuniao [titulo]", desc: "Cria videochamada e envia link ao cliente no WhatsApp" },
  { cmd: "#audiencia [titulo]", desc: "Cria audiência virtual gravada com links por papel" },
  { cmd: "#encerrar-reuniao <ID>", desc: "Encerra uma reunião pelo ID da sessão" },
  { cmd: "#ajuda", desc: "Lista todos os comandos disponíveis no WhatsApp" }
];

const ACTION_META = {
  bbb_meeting_create: { label: "Reunião criada", color: "primary" },
  bbb_hearing_create: { label: "Audiência criada", color: "secondary" },
  bbb_meeting_end: { label: "Reunião encerrada", color: "default" }
};

const LAYOUT_OPTIONS = [
  { value: "", label: "Padrão (Smart Layout)" },
  { value: "SMART_LAYOUT", label: "Smart Layout" },
  { value: "CUSTOM_LAYOUT", label: "Layout Personalizado" },
  { value: "PRESENTATION_FOCUS", label: "Foco na Apresentação" },
  { value: "VIDEO_FOCUS", label: "Foco no Vídeo" },
  { value: "CAMERAS_ONLY", label: "Somente Câmeras" },
  { value: "PRESENTATION_ONLY", label: "Somente Apresentação" }
];

const copyToClipboard = (text) => {
  if (!text) return;
  navigator.clipboard.writeText(text)
    .then(() => toast.info("Link copiado!"))
    .catch(() => toast.error("Erro ao copiar"));
};

const safeParseDetails = (details) => {
  if (!details) return {};
  if (typeof details === "object") return details;
  try {
    return JSON.parse(details);
  } catch (_) {
    return {};
  }
};

const BBBMeetings = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const [meetings, setMeetings] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [joinDialog, setJoinDialog] = useState({ open: false, meeting: null });
  const [endDialog, setEndDialog] = useState({ open: false, meetingID: null });
  const [meetingName, setMeetingName] = useState("");
  const [duration, setDuration] = useState(60);
  const [record, setRecord] = useState(true);
  const [joinName, setJoinName] = useState("");
  const [joinModerator, setJoinModerator] = useState(false);
  const [createdMeeting, setCreatedMeeting] = useState(null);
  const intervalRef = useRef(null);

  // Styling state
  const [showStyling, setShowStyling] = useState(false);
  const [bannerText, setBannerText] = useState("");
  const [bannerColor, setBannerColor] = useState("#4F46E5");
  const [meetingLayout, setMeetingLayout] = useState("");
  const [muteOnStart, setMuteOnStart] = useState(false);
  const [welcomeMsg, setWelcomeMsg] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [autoStartRecording, setAutoStartRecording] = useState(false);
  const [webcamsOnlyForModerator, setWebcamsOnlyForModerator] = useState(false);

  const fetchMeetings = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await listBBBMeetings();
      setMeetings(res.data.meetings || []);
      setLastRefresh(new Date());
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.error || "Erro ao buscar reuniões. Verifique configuração BBB.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (silent = false) => {
    if (!silent) setHistoryLoading(true);
    try {
      const res = await getBBBMeetingHistory({ limit: 12 });
      setHistory(res.data.logs || []);
    } catch (e) {
      if (!silent) toast.error("Erro ao carregar histórico de reuniões");
    } finally {
      if (!silent) setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
    fetchHistory();
    intervalRef.current = setInterval(() => {
      fetchMeetings(true);
      fetchHistory(true);
    }, 20000);
    return () => clearInterval(intervalRef.current);
  }, [fetchMeetings, fetchHistory]);

  useEffect(() => {
    if (!user?.companyId || !socketManager) return;
    const socket = socketManager.getSocket(user.companyId);
    if (!socket) return;
    const companyId = user.companyId;

    const onBBBEvent = (data) => {
      if (data.action === "create" && data.meeting) {
        setMeetings(prev => {
          const exists = prev.find(m => m.meetingID === data.meeting.meetingID);
          return exists ? prev : [data.meeting, ...prev];
        });
        fetchHistory(true);
        setLastRefresh(new Date());
      } else if (data.action === "end" && data.meetingID) {
        setMeetings(prev => prev.filter(m => m.meetingID !== data.meetingID));
        fetchHistory(true);
        setLastRefresh(new Date());
      }
    };

    socket.on(`company-${companyId}-bbb-meeting`, onBBBEvent);
    return () => socket.off(`company-${companyId}-bbb-meeting`, onBBBEvent);
  }, [user, socketManager, fetchHistory]);

  const resetCreateForm = () => {
    setMeetingName("");
    setDuration(60);
    setRecord(true);
    setShowStyling(false);
    setBannerText("");
    setBannerColor("#4F46E5");
    setMeetingLayout("");
    setMuteOnStart(false);
    setWelcomeMsg("");
    setLogoUrl("");
    setAutoStartRecording(false);
    setWebcamsOnlyForModerator(false);
  };

  const handleCreate = async () => {
    try {
      const payload = { meetingName, duration, record };

      if (bannerText) payload.bannerText = bannerText;
      if (bannerText && bannerColor) payload.bannerColor = bannerColor;
      if (meetingLayout) payload.meetingLayout = meetingLayout;
      if (muteOnStart) payload.muteOnStart = true;
      if (welcomeMsg) payload.welcome = welcomeMsg;
      if (logoUrl) payload.logo = logoUrl;
      if (autoStartRecording) payload.autoStartRecording = true;
      if (webcamsOnlyForModerator) payload.webcamsOnlyForModerator = true;

      const res = await createBBBMeeting(payload);
      setCreatedMeeting(res.data);
      setCreateDialog(false);
      toast.success("Reunião criada!");
      setTimeout(() => {
        fetchMeetings(true);
        fetchHistory(true);
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao criar reunião. Verifique configuração BBB.");
    }
  };

  const handleJoin = async () => {
    if (!joinName.trim()) {
      toast.warning("Informe seu nome");
      return;
    }
    try {
      const res = await joinBBBMeeting({
        meetingID: joinDialog.meeting?.meetingID,
        fullName: joinName,
        isModerator: joinModerator
      });
      window.open(res.data.url, "_blank", "noopener,noreferrer");
      setJoinDialog({ open: false, meeting: null });
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao entrar na reunião");
    }
  };

  const handleEnd = async () => {
    try {
      await endBBBMeeting(endDialog.meetingID);
      setMeetings(prev => prev.filter(m => m.meetingID !== endDialog.meetingID));
      setEndDialog({ open: false, meetingID: null });
      fetchHistory(true);
      toast.success("Reunião encerrada");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao encerrar reunião");
    }
  };

  const formatStartTime = (ts) => {
    if (!ts) return "-";
    const d = new Date(Number(ts));
    return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString("pt-BR");
  };

  const runningMeetings = meetings.filter(m => m.running);
  const waitingMeetings = meetings.filter(m => !m.running);
  const createdCount = history.filter(item => item.action === "bbb_meeting_create" || item.action === "bbb_hearing_create").length;

  const enrichedHistory = useMemo(() => history.map((log) => {
    const details = safeParseDetails(log.details);
    return {
      ...log,
      parsedDetails: details,
      title: details.meetingName || details.hearingName || log.entityId || "Reunião sem título",
      meetingID: details.meetingID || log.entityId,
      attendeeUrl: details.attendeeUrl,
      moderatorUrl: details.moderatorUrl,
      source: details.source === "whatsapp_command" ? "WhatsApp" : (details.source === "bbb_panel" ? "Painel" : "Sistema")
    };
  }), [history]);

  return (
    <Container maxWidth="lg" className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5">
          <VideoCall style={{ verticalAlign: "middle", marginRight: 8 }} />
          Reuniões BBB
          {runningMeetings.length > 0 && (
            <span className={classes.liveBadge}>
              <FiberManualRecord style={{ fontSize: 10, marginRight: 4 }} />
              {runningMeetings.length} ao vivo
            </span>
          )}
        </Typography>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {lastRefresh && <span className={classes.refreshText}>Atualizado: {lastRefresh.toLocaleTimeString("pt-BR")}</span>}
          <Tooltip title="Atualizar lista e histórico">
            <IconButton size="small" onClick={() => { fetchMeetings(false); fetchHistory(false); }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<VideoCall />}
            onClick={() => { resetCreateForm(); setCreateDialog(true); }}
          >
            Nova Reunião
          </Button>
        </div>
      </div>

      <Grid container spacing={2} style={{ marginBottom: 8 }}>
        <Grid item xs={12} sm={4}>
          <Paper className={classes.statCard}>
            <div className={classes.statLabel}>Reuniões ativas agora</div>
            <div className={classes.statValue}>{runningMeetings.length}</div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper className={classes.statCard}>
            <div className={classes.statLabel}>Criadas recentemente</div>
            <div className={classes.statValue}>{createdCount}</div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper className={classes.statCard}>
            <div className={classes.statLabel}>Origem principal</div>
            <div className={classes.statValue} style={{ fontSize: 18 }}>
              {enrichedHistory.some(item => item.source === "WhatsApp") ? "WhatsApp" : "Painel"}
            </div>
          </Paper>
        </Grid>
      </Grid>

      <Paper className={classes.whatsappSection} elevation={0}>
        <Typography variant="subtitle2" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, fontWeight: 700, color: "#2e7d32" }}>
          <WhatsApp fontSize="small" /> Comandos WhatsApp para Reuniões
        </Typography>
        <Grid container spacing={1}>
          {COMMANDS.map(({ cmd, desc }) => (
            <Grid item xs={12} sm={6} key={cmd}>
              <div className={classes.commandBox}>
                <span className={classes.commandLabel}>{cmd}</span>
                <span className={classes.commandDesc}>{desc}</span>
              </div>
            </Grid>
          ))}
        </Grid>
        <Typography variant="caption" style={{ color: "#555", marginTop: 4, display: "block" }}>
          Digite estes comandos no chat do WhatsApp durante um atendimento. As reuniões ativas aparecem em tempo real e o histórico recente fica disponível abaixo.
        </Typography>
      </Paper>

      {createdMeeting && (
        <Paper style={{ padding: 16, marginBottom: 16, background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 12 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            Reunião criada com sucesso — {createdMeeting.meetingID}
          </Typography>
          <Grid container spacing={1} style={{ marginTop: 8 }} alignItems="center">
            <Grid item>
              <Button variant="contained" color="primary" size="small" startIcon={<MeetingRoom />} href={createdMeeting.joinUrlModerator} target="_blank" rel="noopener noreferrer">
                Entrar como Moderador
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" color="primary" size="small" startIcon={<ExitToApp />} href={createdMeeting.joinUrlAttendee} target="_blank" rel="noopener noreferrer">
                Link do Participante
              </Button>
            </Grid>
            <Grid item>
              <Tooltip title="Copiar link do participante">
                <IconButton size="small" onClick={() => copyToClipboard(createdMeeting.joinUrlAttendee)}>
                  <FileCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item>
              <IconButton size="small" onClick={() => setCreatedMeeting(null)}><Close fontSize="small" /></IconButton>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper className={classes.sectionPaper}>
        <Typography className={classes.sectionTitle}>
          Reuniões Ativas {loading && <CircularProgress size={14} style={{ marginLeft: 8 }} />}
        </Typography>
        <Divider style={{ marginBottom: 12 }} />
        {runningMeetings.length === 0 && !loading && (
          <div className={classes.emptyState}>
            <VideoCall style={{ fontSize: 48, marginBottom: 8 }} />
            <Typography>Nenhuma reunião ativa no momento</Typography>
            <Typography variant="caption">Use <strong>#reuniao [titulo]</strong> no WhatsApp ou clique em "Nova Reunião".</Typography>
          </div>
        )}
        {runningMeetings.map(m => (
          <div key={m.meetingID} className={classes.meetingCard}>
            <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  <FiberManualRecord className={classes.runningDot} />
                  {m.meetingName}
                </Typography>
                <div className={classes.metaLine}>
                  ID: {m.meetingID} • Iniciada: {formatStartTime(m.startTime)} • Participantes: {m.participantCount} ({m.moderatorCount} moderadores)
                </div>
              </Grid>
              <Grid item>
                <Button size="small" variant="contained" color="primary" startIcon={<MeetingRoom />} style={{ marginRight: 8 }} onClick={() => { setJoinName(user?.name || ""); setJoinModerator(false); setJoinDialog({ open: true, meeting: m }); }}>
                  Entrar
                </Button>
                <Button size="small" variant="outlined" color="secondary" startIcon={<Stop />} onClick={() => setEndDialog({ open: true, meetingID: m.meetingID })}>
                  Encerrar
                </Button>
              </Grid>
            </Grid>
          </div>
        ))}
      </Paper>

      {waitingMeetings.length > 0 && (
        <Paper className={classes.sectionPaper}>
          <Typography className={classes.sectionTitle}>Aguardando participantes</Typography>
          <Divider style={{ marginBottom: 12 }} />
          {waitingMeetings.map(m => (
            <div key={m.meetingID} className={classes.meetingCard}>
              <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    <FiberManualRecord className={classes.stoppedDot} />
                    {m.meetingName}
                    <span className={classes.inactiveBadge}>aguardando</span>
                  </Typography>
                  <div className={classes.metaLine}>ID: {m.meetingID} • Participantes: {m.participantCount}</div>
                </Grid>
                <Grid item>
                  <Button size="small" variant="outlined" color="primary" startIcon={<MeetingRoom />} style={{ marginRight: 8 }} onClick={() => { setJoinName(user?.name || ""); setJoinModerator(false); setJoinDialog({ open: true, meeting: m }); }}>
                    Entrar
                  </Button>
                  <Button size="small" variant="outlined" color="secondary" startIcon={<Stop />} onClick={() => setEndDialog({ open: true, meetingID: m.meetingID })}>
                    Encerrar
                  </Button>
                </Grid>
              </Grid>
            </div>
          ))}
        </Paper>
      )}

      <Paper className={classes.sectionPaper}>
        <Typography className={classes.sectionTitle}>
          <History fontSize="small" style={{ verticalAlign: "middle", marginRight: 6 }} /> Histórico recente {historyLoading && <CircularProgress size={14} style={{ marginLeft: 8 }} />}
        </Typography>
        <Divider style={{ marginBottom: 12 }} />
        {enrichedHistory.length === 0 && !historyLoading && (
          <div className={classes.emptyState}>
            <Typography>Nenhum histórico recente encontrado</Typography>
            <Typography variant="caption">Quando uma reunião for criada via WhatsApp ou painel, ela aparecerá aqui.</Typography>
          </div>
        )}
        <Grid container spacing={2}>
          {enrichedHistory.map(item => {
            const meta = ACTION_META[item.action] || { label: item.action, color: "default" };
            return (
              <Grid item xs={12} md={6} key={item.id}>
                <div className={classes.historyCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                    <div>
                      <Typography variant="subtitle2" style={{ fontWeight: 700 }}>{item.title}</Typography>
                      <div className={classes.smallMuted}>ID: {item.meetingID}</div>
                    </div>
                    <Chip size="small" color={meta.color === "default" ? undefined : meta.color} label={meta.label} />
                  </div>
                  <div className={classes.metaLine}>Criada em {new Date(item.createdAt).toLocaleString("pt-BR")}</div>
                  <div className={classes.metaLine}>Origem: {item.source}</div>
                  <div className={classes.historyActions}>
                    {item.attendeeUrl && (
                      <>
                        <Button size="small" color="primary" startIcon={<ExitToApp />} href={item.attendeeUrl} target="_blank" rel="noopener noreferrer">
                          Abrir link
                        </Button>
                        <IconButton size="small" onClick={() => copyToClipboard(item.attendeeUrl)}>
                          <FileCopy fontSize="small" />
                        </IconButton>
                      </>
                    )}
                    {item.meetingID && (
                      <Button size="small" startIcon={<LinkIcon />} onClick={() => copyToClipboard(`#encerrar-reuniao ${item.meetingID}`)}>
                        Copiar comando de encerramento
                      </Button>
                    )}
                  </div>
                </div>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* CREATE DIALOG */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Reunião BBB</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Nome da Reunião" value={meetingName} onChange={e => setMeetingName(e.target.value)} placeholder="Ex: Consulta Jurídica - João Silva" autoFocus />
          <TextField fullWidth margin="dense" label="Duração máxima (minutos)" type="number" value={duration} onChange={e => setDuration(Number(e.target.value))} inputProps={{ min: 10, max: 480 }} />
          <FormControlLabel control={<Switch checked={record} onChange={e => setRecord(e.target.checked)} color="primary" />} label="Gravar reunião" />

          {/* Styling Section */}
          <div className={classes.stylingSection}>
            <div className={classes.stylingHeader} onClick={() => setShowStyling(!showStyling)}>
              <Palette fontSize="small" />
              <Typography variant="subtitle2" style={{ fontWeight: 600, flex: 1 }}>Personalização da Reunião</Typography>
              {showStyling ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </div>
            <Collapse in={showStyling}>
              <div style={{ marginTop: 12 }}>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
                  Configure a aparência e comportamento da sala. Valores em branco usam os padrões da marca.
                </Typography>

                <TextField
                  fullWidth margin="dense" size="small"
                  label="Texto do Banner"
                  value={bannerText}
                  onChange={e => setBannerText(e.target.value)}
                  placeholder="Ex: FACILE MACILE VMeet — Reunião Confidencial"
                  helperText="Exibido como faixa no topo da sala"
                />

                <div className={classes.colorInputRow} style={{ marginTop: 4 }}>
                  <input
                    type="color"
                    value={bannerColor}
                    onChange={e => setBannerColor(e.target.value)}
                    className={classes.colorSwatch}
                    title="Cor do banner"
                  />
                  <TextField
                    margin="dense" size="small"
                    label="Cor do Banner"
                    value={bannerColor}
                    onChange={e => setBannerColor(e.target.value)}
                    placeholder="#4F46E5"
                    style={{ flex: 1 }}
                    helperText="Formato hexadecimal (#RRGGBB)"
                  />
                </div>

                <TextField
                  fullWidth margin="dense" size="small"
                  label="URL do Logo"
                  value={logoUrl}
                  onChange={e => setLogoUrl(e.target.value)}
                  placeholder="https://bbb.damineweb.work/images/facile-macile-logo.png"
                  helperText="Imagem exibida acima da lista de participantes"
                />

                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel>Layout da Sala</InputLabel>
                  <Select value={meetingLayout} onChange={e => setMeetingLayout(e.target.value)} label="Layout da Sala">
                    {LAYOUT_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth margin="dense" size="small" multiline rows={2}
                  label="Mensagem de Boas-Vindas (HTML)"
                  value={welcomeMsg}
                  onChange={e => setWelcomeMsg(e.target.value)}
                  placeholder="Bem-vindo à reunião <b>%%CONFNAME%%</b>!"
                  helperText="Suporta HTML básico. Use %%CONFNAME%% para o nome da reunião."
                />

                <Divider style={{ margin: "8px 0" }} />
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>Comportamento</Typography>

                <div>
                  <FormControlLabel
                    control={<Switch checked={muteOnStart} onChange={e => setMuteOnStart(e.target.checked)} color="primary" size="small" />}
                    label={<Typography variant="body2">Mutar todos ao entrar</Typography>}
                  />
                </div>
                <div>
                  <FormControlLabel
                    control={<Switch checked={autoStartRecording} onChange={e => setAutoStartRecording(e.target.checked)} color="primary" size="small" />}
                    label={<Typography variant="body2">Iniciar gravação automaticamente</Typography>}
                  />
                </div>
                <div>
                  <FormControlLabel
                    control={<Switch checked={webcamsOnlyForModerator} onChange={e => setWebcamsOnlyForModerator(e.target.checked)} color="primary" size="small" />}
                    label={<Typography variant="body2">Câmeras visíveis só para moderadores</Typography>}
                  />
                </div>
              </div>
            </Collapse>
          </div>

          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
            A gravação ficará disponível no servidor BBB após o encerramento.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancelar</Button>
          <Button onClick={handleCreate} variant="contained" color="primary" disabled={!meetingName.trim()}>
            Criar Reunião
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={joinDialog.open} onClose={() => setJoinDialog({ open: false, meeting: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Entrar na Reunião</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>{joinDialog.meeting?.meetingName}</Typography>
          <TextField fullWidth margin="dense" label="Seu nome na reunião" value={joinName} onChange={e => setJoinName(e.target.value)} autoFocus />
          <FormControlLabel control={<Switch checked={joinModerator} onChange={e => setJoinModerator(e.target.checked)} color="primary" />} label="Entrar como Moderador" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinDialog({ open: false, meeting: null })}>Cancelar</Button>
          <Button onClick={handleJoin} variant="contained" color="primary" disabled={!joinName.trim()}>
            Entrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={endDialog.open} onClose={() => setEndDialog({ open: false, meetingID: null })} maxWidth="xs">
        <DialogTitle>Encerrar Reunião</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja encerrar a reunião <strong>{endDialog.meetingID}</strong>? Todos os participantes serão desconectados imediatamente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDialog({ open: false, meetingID: null })}>Cancelar</Button>
          <Button onClick={handleEnd} variant="contained" color="secondary">Encerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BBBMeetings;
